import * as geojson from 'geojson';

import {
  NOTIFICATION_SCOPE_ALL,
  NOTIFICATION_SCOPE_MINE,
  NOTIFICATION_SCOPE_NONE,
} from 'src/lib/constants';
import featureFlags from 'src/lib/featureFlags';

import {makeApiRequest} from './api';
import {mockProjectData} from './mocks';
import {
  AllColumns,
  DashboardSection,
  ForecastTimeSeriesQuery,
  ObservationTimeSeriesQuery,
  RateVolumeMode,
  UnitSystem,
} from './series';

export enum SeriesType {
  GaugeFlow = 'gauge-flow',
  GaugeElevation = 'gauge-elevation',
  FlowShortTerm = 'flow-short-term',
  Level = 'level',
  FlowSeasonalRolling = 'flow-seasonal-rolling',
  FlowSeasonalRollingExtended = 'flow-seasonal-rolling-extended',
  WeatherShortTerm = 'weather-short-term',
  WeatherSeasonalRolling = 'weather-seasonal-rolling',
  SurfaceObservations = 'surface-observations',
}

export type SeasonalSeriesType =
  | SeriesType.FlowSeasonalRolling
  | SeriesType.FlowSeasonalRollingExtended;

export enum SiteType {
  BASIN = 'basin',
  BASIN_COMBINED = 'basin-combined',
  BASIN_ROUTED = 'basin-routed',
  LOCATION = 'location',
  SUBBASIN = 'subbasin',
  GAUGE = 'gauge-location',
}

// Backend response types (partial - only includes present series types)
export type ApiForecastTimeSeries = Partial<Record<SeriesType, ForecastTimeSeriesQuery[]>>;
export type ApiObservationTimeSeries = Partial<Record<SeriesType, ObservationTimeSeriesQuery[]>>;

// Frontend types (normalized - includes all series types with empty arrays for missing ones)
export type ForecastTimeSeries = Record<SeriesType, ForecastTimeSeriesQuery[]>;
export type ObservationTimeSeries = Record<SeriesType, ObservationTimeSeriesQuery[]>;

/**
 * The backend returns a dictionary with keys only for the present
 * series types. However in most places on the frontend, we want
 * a full record, with empty lists if values are not present, to
 * avoid optional chaining everywhere.
 * This function fills out the partial.
 */
export function normalizeTimeSeries<T>(
  partialTimeSeries: Partial<Record<SeriesType, T[]>>
): Record<SeriesType, T[]> {
  const normalized = {} as Record<SeriesType, T[]>;

  // Initialize all series types with empty arrays
  Object.values(SeriesType).forEach((seriesType) => {
    normalized[seriesType] = [];
  });

  // Override with actual data where present
  Object.entries(partialTimeSeries).forEach(([seriesType, data]) => {
    if (data) {
      normalized[seriesType as SeriesType] = data;
    }
  });

  return normalized;
}

type ApiSite = {
  // The sections H5 has a publication for at this site, customer-visible or not. Sent to internal
  // admins only; it is what the H5 preview can widen the dashboard to (see h5DashboardSectionsFor).
  h5Sections?: DashboardSection[];
  id: string;
  name: string;
  // Sites on migrated (H5) projects carry only id/name/geometry; the other
  // fields are absent and get normalized to empty values on the frontend.
  siteType?: SiteType;
  geometry?: geojson.Geometry;
  seriesTypes?: SeriesType[];
  observationTimeSeries?: ApiObservationTimeSeries;
  forecastTimeSeries?: ApiForecastTimeSeries;
  featureIds?: {
    drainageAreaFeatureId?: ApiFeature['id'];
    forecastPointFeatureId?: ApiFeature['id'];
  };
  localTimeZone?: string;
  defaultRateVolumeMode?: RateVolumeMode;
};

export type Site = ApiSite & {
  projectId: Project['id'];
  seriesTypes: SeriesType[];
  forecastTimeSeries: ForecastTimeSeries;
  observationTimeSeries: ObservationTimeSeries;
  featureIds: NonNullable<ApiSite['featureIds']>;
  localTimeZone: string;
};

export type ThresholdValue = {
  critical?: number;
  warning?: number;
};

export type Thresholds = {
  above?: ThresholdValue;
  below?: ThresholdValue;
};

type ApiProject = {
  id: string;
  name: string;
  sites: null | ApiSite[];
  unitSystem: UnitSystem;
  defaultDateRange: [Date, Date] | undefined;
  isHidden: boolean;
  basinAveragingEnabled: boolean;
  units: {[key in AllColumns]: string};
  // The sections customers see from H5 — the same list the backend routes queries on
  // (see resolve_query_backend). Absent on projects with no migrated config, which reads as no
  // section on H5.
  customerVisibleDashboardSections?: DashboardSection[];
};

export type Project = Omit<ApiProject, 'sites'> & {
  sites: null | Site[];
};

export type ApiFeature = geojson.Feature<geojson.Geometry, any> & {
  id: number;
};

export type NotificationScope =
  | typeof NOTIFICATION_SCOPE_ALL
  | typeof NOTIFICATION_SCOPE_MINE
  | typeof NOTIFICATION_SCOPE_NONE;

export type ApiFlowContributingAreas = {
  [siteId: Site['id']]: {
    featureId: ApiFeature['id'];
    siteId: Site['id'];
  }[];
};

export async function listProjects(): Promise<Project[]> {
  const response = await makeApiRequest<Project[]>('api/v2/hydroforecast/projects');
  return response.data;
}

function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
}

export async function fetchProject(projectId: Project['id'], h5Preview = false): Promise<Project> {
  const response = await makeApiRequest<Project>(
    `api/v2/hydroforecast/projects/${projectId}`,
    h5Preview ? {h5Preview: '1'} : null
  );
  return normalizeProjectResponse(response.data);
}

export async function fetchProjectWithContextOrg(
  projectId: Project['id'],
  contextOrgId: string
): Promise<Project> {
  const response = await makeApiRequest<Project>(
    `api/v2/hydroforecast/projects/${projectId}`,
    {cOrgId: contextOrgId},
    {},
    {noContextOrg: true}
  );
  return normalizeProjectResponse(response.data);
}

export function normalizeProjectResponse(project: Project): Project {
  let p = project;

  if (featureFlags.USE_API_MOCKING) {
    p = mockProjectData(p);
  }

  const sites = p.sites
    ? p.sites.map((site) => ({
        ...site,
        projectId: p.id,
        seriesTypes: site.seriesTypes ?? [],
        forecastTimeSeries: normalizeTimeSeries(site.forecastTimeSeries ?? {}),
        observationTimeSeries: normalizeTimeSeries(site.observationTimeSeries ?? {}),
        featureIds: site.featureIds ?? {},
        localTimeZone: site.localTimeZone ?? 'UTC',
        name: site.name ? toTitleCase(site.name) : site.name,
      }))
    : null;
  const defaultDateRange = p.defaultDateRange
    ? ([new Date(p.defaultDateRange[0]), new Date(p.defaultDateRange[1])] as [Date, Date])
    : undefined;

  return {
    ...p,
    sites: sites,
    defaultDateRange,
  };
}

export async function batchFetchFeatures(featureIds: ApiFeature['id'][]): Promise<ApiFeature[]> {
  const features: ApiFeature[] = [];
  let page = 1;
  const filteredFeatureIds = featureIds.filter((fId) => !!fId);
  while (true) {
    const response = await makeApiRequest<ApiFeature[]>('/api/v2/hydroforecast/features/batch', {
      id: filteredFeatureIds,
      page,
    });
    if (response.data.length === 0) {
      return features;
    } else {
      features.push(...response.data);
      page++;
    }
  }
}

export async function fetchFlowContributingAreas(
  projectId: Project['id']
): Promise<ApiFlowContributingAreas> {
  const response = await makeApiRequest<ApiFlowContributingAreas>(
    `api/v2/flowContributingAreas/${projectId}`
  );
  return response.data;
}
